import React, { useMemo } from 'react';
import type {
  ColDef,
  ValueGetterParams,
  ValueSetterParams,
} from 'ag-grid-community';
import { LopCrudPage } from '../../components/LopCrudPage';
import {
  getAssetTypeDefaultValues,
  saveAssetTypeDefaultValuesBatch,
} from '../../api/assetTypeDefaultValues';
import { useAssetTypes } from '../../hooks/useAssetTypes';
import type { AssetTypeDefaultValue } from '../../types/AssetTypeDefaultValue';
import { t } from '../../i18n/t';

const newDefaults = (): Partial<AssetTypeDefaultValue> => ({
  recId: 0,
  assetTypeId: 0,
  targetColumn: '',
  defaultValue: '',
  conditionColumn: null,
  conditionValue: null,
  isActive: true,
});

export function AssetTypeDefaultValuesPage() {
  // assetTypeId dropdown reuses the same session-cached lookup as AssetsPage.
  const { data: assetTypes, loading, error } = useAssetTypes();

  const columnDefs = useMemo<ColDef<AssetTypeDefaultValue>[]>(() => {
    if (!assetTypes) return [];

    // Names are NOT unique in AssetTypes (e.g. "מבוטל" appears twice), so the
    // dropdown is keyed by a unique "<code> - <name>" label — same pattern as
    // AssetsPage.assetTypeId. Required (NOT NULL): no "none" option.
    const assetTypeValues = assetTypes.map(
      (a) => `${a.assetTypeId} - ${a.assetTypeName}`,
    );
    const assetTypeLabelById = new Map<number, string>(
      assetTypes.map((a) => [a.assetTypeId, `${a.assetTypeId} - ${a.assetTypeName}`]),
    );
    const assetTypeIdByLabel = new Map<string, number>(
      assetTypes.map((a) => [`${a.assetTypeId} - ${a.assetTypeName}`, a.assetTypeId]),
    );

    return [
      {
        field: 'recId',
        headerName: t('assetTypeDefaultValues.columns.recId'),
        width: 90,
        editable: false,
        cellDataType: 'number',
      },
      {
        field: 'assetTypeId',
        headerName: t('assetTypeDefaultValues.columns.assetTypeId'),
        width: 200,
        editable: true,
        cellDataType: 'text',
        cellEditor: 'agSelectCellEditor',
        cellEditorParams: { values: assetTypeValues },
        valueGetter: (p: ValueGetterParams<AssetTypeDefaultValue>) => {
          if (!p.data) return '';
          // REQUIRED (NOT NULL) — fall back to the raw id for dirty/orphan data.
          return (
            assetTypeLabelById.get(p.data.assetTypeId) ??
            String(p.data.assetTypeId)
          );
        },
        valueSetter: (p: ValueSetterParams<AssetTypeDefaultValue>) => {
          if (!p.data) return false;
          const newId = assetTypeIdByLabel.get(p.newValue);
          if (newId === undefined) return false;
          p.data.assetTypeId = newId;
          return true;
        },
      },
      {
        field: 'targetColumn',
        headerName: t('assetTypeDefaultValues.columns.targetColumn'),
        headerTooltip: t('assetTypeDefaultValues.tooltips.targetColumn'),
        width: 200,
        editable: true,
      },
      {
        field: 'defaultValue',
        headerName: t('assetTypeDefaultValues.columns.defaultValue'),
        width: 160,
        editable: true,
      },
      {
        field: 'conditionColumn',
        headerName: t('assetTypeDefaultValues.columns.conditionColumn'),
        headerTooltip: t('assetTypeDefaultValues.tooltips.conditionColumn'),
        width: 180,
        editable: true,
      },
      {
        field: 'conditionValue',
        headerName: t('assetTypeDefaultValues.columns.conditionValue'),
        width: 160,
        editable: true,
      },
      {
        field: 'isActive',
        headerName: t('assetTypeDefaultValues.columns.isActive'),
        headerTooltip: t('assetTypeDefaultValues.tooltips.isActive'),
        width: 100,
        editable: true,
        cellDataType: 'boolean',
      },
    ];
  }, [assetTypes]);

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <div
          style={{
            color: 'var(--color-error-text)',
            background: 'var(--color-error-bg)',
            padding: '8px 12px',
            borderRadius: 4,
          }}
        >
          {t('assetTypeDefaultValues.errorPrefix', { message: error })}
        </div>
      </div>
    );
  }

  if (loading || !assetTypes) {
    return (
      <div style={{ padding: 16, color: 'var(--color-text-secondary)' }}>{t('common.loading')}</div>
    );
  }

  return (
    <LopCrudPage<AssetTypeDefaultValue>
      resourceCode="assetTypeDefaultValues"
      titleKey="assetTypeDefaultValues.pageTitle"
      idField="recId"
      getRows={getAssetTypeDefaultValues}
      saveBatch={(added, modified, deletedIds) =>
        saveAssetTypeDefaultValuesBatch(added, modified, deletedIds as number[])
      }
      columnDefs={columnDefs}
      newRowDefaults={newDefaults}
    />
  );
}
